/**
 * Lumistelle Carousel Video Module
 * Version 4.0 - Simplified & Stable
 * Create on enter, destroy on exit. No clever tricks.
 */

(function(window, document) {
  'use strict';

  // ======================================================================
  // DEBUG MODE (only with ?video-debug)
  // ======================================================================
  const DEBUG = window.location.search.includes('video-debug');
  const log = DEBUG ? console.log.bind(console) : () => {};

  // ======================================================================
  // DEVICE DETECTION
  // ======================================================================
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) 
      || window.innerWidth < 768;
  };

  // ======================================================================
  // MAIN VIDEO SYSTEM
  // ======================================================================
  const LumistelleCarouselVideo = {
    // State
    systemReady: false,
    delayTimerStarted: false,
    activeVideos: new Map(), // Only playing videos
    allVideos: new Set(), // All video elements in DOM
    loadingDelay: 5000,
    observers: new Map(),
    cleanupIntervalId: null, // PHASE 1 FIX: Periodic cleanup timer
    isScrolling: false, // PHASE 2 FIX: Scroll state tracking
    scrollTimeout: null, // PHASE 2 FIX: Scroll debounce timer
    pendingVideoCreations: [], // PHASE 2 FIX: Queue for delayed video creation
    loadingQueue: [], // PHASE 2 FIX: Queue for sequential video loading
    currentlyLoading: false, // PHASE 2 FIX: Loading state flag
    
    // Settings
    getMaxVideosInDOM() {
      return isMobileDevice() ? 2 : 6;
    },
    
    getObserverConfig() {
      const isMobile = isMobileDevice();
      return {
        threshold: 0.5,
        rootMargin: isMobile ? '100px' : '100px' // PHASE 2 FIX: Reduced from 200px to 100px on mobile
      };
    },

    // ======================================================================
    // SCROLL THROTTLING SYSTEM (PHASE 2 FIX)
    // ======================================================================
    startScrollMonitoring() {
      if (this.scrollMonitoringActive) return;
      this.scrollMonitoringActive = true;
      
      const handleScroll = () => {
        this.isScrolling = true;
        
        // Clear existing timeout
        if (this.scrollTimeout) {
          clearTimeout(this.scrollTimeout);
        }
        
        // Set timeout to detect when scrolling stops
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
          this.processPendingVideoCreations();
        }, 150); // Wait 150ms after scroll stops
      };
      
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('touchmove', handleScroll, { passive: true });
      
      log('[Video] Scroll monitoring started');
    },

    processPendingVideoCreations() {
      if (this.pendingVideoCreations.length === 0) return;
      
      log(`[Video] Processing ${this.pendingVideoCreations.length} pending video creations`);
      
      // Process all pending video creations
      const pending = [...this.pendingVideoCreations];
      this.pendingVideoCreations = [];
      
      pending.forEach(({ container, productCard, instance }) => {
        if (this.shouldCreateVideo(container)) {
          this.createVideoElement(container, productCard, instance);
        }
      });
    },

    shouldCreateVideo(container) {
      // Check if container is still in viewport
      const rect = container.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      return isInViewport && container.offsetParent !== null;
    },

    // ======================================================================
    // VIDEO LOADING QUEUE (PHASE 2 FIX - MOBILE ONLY)
    // ======================================================================
    queueVideoLoad(video, container) {
      if (!isMobileDevice()) {
        // Desktop: load immediately
        video.load();
        return;
      }
      
      // Mobile: use queue to load one at a time
      this.loadingQueue.push({ video, container });
      this.processLoadingQueue();
    },

    processLoadingQueue() {
      // If already loading or queue is empty, do nothing
      if (this.currentlyLoading || this.loadingQueue.length === 0) {
        return;
      }
      
      // Get next video from queue
      const next = this.loadingQueue.shift();
      if (!next) return;
      
      const { video, container } = next;
      
      // Check if video is still relevant (not destroyed)
      if (!this.allVideos.has(video) || !container.dataset.videoCreated) {
        // Video was destroyed, move to next
        this.processLoadingQueue();
        return;
      }
      
      this.currentlyLoading = true;
      log('[Video] Loading from queue:', this.loadingQueue.length, 'remaining');
      
      // Set up completion handlers
      const onLoadComplete = () => {
        this.currentlyLoading = false;
        // Process next video after a small delay
        setTimeout(() => this.processLoadingQueue(), 100);
      };
      
      // Listen for load completion
      video.addEventListener('canplay', onLoadComplete, { once: true });
      video.addEventListener('error', onLoadComplete, { once: true });
      
      // Timeout fallback
      setTimeout(onLoadComplete, 5000);
      
      // Start loading
      video.load();
    },

    // ======================================================================
    // INITIALIZATION
    // ======================================================================
    initialize(instance) {
      if (!instance || !(instance.element || instance.container)) return;
      
      const element = instance.element || instance.container;
      this.loadingDelay = (instance.config.videoLoadingDelay || 5) * 1000;

      // Start delay timer if needed
      if (this.systemReady) {
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('lumistelle:video:system-ready'));
        }, 50);
      } else if (!this.delayTimerStarted) {
        this.startDelayTimer();
      }

      // Setup observers
      this.setupVideoObservers(instance);

      // PHASE 1 FIX: Start periodic cleanup if not already running
      this.startPeriodicCleanup();

      // PHASE 2 FIX: Start scroll monitoring on mobile
      if (isMobileDevice()) {
        this.startScrollMonitoring();
      }
      
      const instanceId = instance.id || element.id;
      log(`[Video] Initialize ${instanceId} (Mobile: ${isMobileDevice()}, Max: ${this.getMaxVideosInDOM()})`);
    },

    startDelayTimer() {
      if (this.delayTimerStarted) return;
      this.delayTimerStarted = true;
      
      setTimeout(() => {
        this.systemReady = true;
        window.lumistelleVideoSystemReady = true;
        log('%c[Video] System Ready', 'color: green; font-weight: bold;');
        document.dispatchEvent(new CustomEvent('lumistelle:video:system-ready'));
      }, this.loadingDelay);
    },

    // ======================================================================
    // PERIODIC CLEANUP SYSTEM (PHASE 1 FIX)
    // ======================================================================
    startPeriodicCleanup() {
      // Only start one cleanup interval globally
      if (this.cleanupIntervalId) return;
      
      this.cleanupIntervalId = setInterval(() => {
        this.performPeriodicCleanup();
      }, 3000); // Run every 3 seconds
      
      log('[Video] Periodic cleanup started');
    },

    performPeriodicCleanup() {
      const maxVideos = this.getMaxVideosInDOM();
      const allVideoElements = Array.from(this.allVideos);
      
      // Find hidden videos (not in viewport)
      const hiddenVideos = allVideoElements.filter(video => {
        const container = video.closest('[data-media-type="video"]');
        if (!container) return true; // Orphaned video
        
        const rect = container.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        const isVisible = container.offsetParent !== null;
        
        return !isInViewport || !isVisible;
      });
      
      // Remove all hidden videos
      if (hiddenVideos.length > 0) {
        log(`[Video] Cleanup: Removing ${hiddenVideos.length} hidden videos`);
        hiddenVideos.forEach(video => {
          const container = video.closest('[data-media-type="video"]');
          if (container) {
            this.destroyVideo(container);
          }
        });
      }
      
      // Safety check: If we exceed max videos, remove oldest
      if (this.allVideos.size > maxVideos) {
        const excessCount = this.allVideos.size - maxVideos;
        log(`[Video] Cleanup: ${this.allVideos.size} videos exceeds max ${maxVideos}, removing ${excessCount}`);
        
        for (let i = 0; i < excessCount; i++) {
          this.cleanupOldestVideo();
        }
      }
    },

    stopPeriodicCleanup() {
      if (this.cleanupIntervalId) {
        clearInterval(this.cleanupIntervalId);
        this.cleanupIntervalId = null;
        log('[Video] Periodic cleanup stopped');
      }
    },

    // ======================================================================
    // OBSERVER SETUP - SIMPLE & DIRECT
    // ======================================================================
    setupVideoObservers(instance) {
      const observerConfig = this.getObserverConfig();
      
      const observer = new IntersectionObserver((entries) => {
        // Process immediately, no throttling
        entries.forEach(entry => {
          const container = entry.target;
          const productCard = container.closest('.lumistelle-carousel__product') || 
                              container.closest('[data-section-id]');
          
          if (entry.isIntersecting) {
            // Video entering viewport
            if (this.systemReady) {
              this.handleVideoEnterViewport(container, productCard, instance);
            } else {
              container.dataset.queuedForLoad = 'true';
            }
          } else {
            // Video exiting viewport
            this.handleVideoExitViewport(container, productCard);
          }
        });
      }, observerConfig);

      const element = instance.element || instance.container;
      const instanceId = instance.id || element.id;
      this.observers.set(instanceId, observer);
      
      // Observe all video containers
      const videoContainers = element.querySelectorAll('[data-media-type="video"]');
      videoContainers.forEach(container => observer.observe(container));

      // Handle system ready event
      const readyHandler = () => {
        const queuedForLoad = element.querySelectorAll('[data-queued-for-load="true"]');
        queuedForLoad.forEach(container => {
          const productCard = container.closest('.lumistelle-carousel__product') || 
                              container.closest('[data-section-id]');
          this.handleVideoEnterViewport(container, productCard, instance);
        });

        const queuedForPlay = element.querySelectorAll('[data-queued-for-play="true"]');
        queuedForPlay.forEach(container => {
          this.handleMediaVisibilityChange(container, true, instance);
        });
      };

      document.addEventListener('lumistelle:video:system-ready', readyHandler, { once: true });
    },

    // ======================================================================
    // VIDEO LIFECYCLE - SIMPLE CREATE/DESTROY
    // ======================================================================
    handleVideoEnterViewport(container, productCard, instance) {
      if (!productCard?.productData) {
        container.dataset.pendingHydration = 'true';
        log('[Video] No product data, queuing for hydration');
        return;
      }

      // If video already exists and is valid, just ensure it's visible
      if (container.dataset.videoCreated === 'true') {
        const existingVideo = container.querySelector('video');
        if (existingVideo && this.allVideos.has(existingVideo)) {
          log('[Video] Reusing existing video');
          if (existingVideo.paused && instance.config.videoAutoplay !== false) {
            this.playVideo(existingVideo, container);
          }
          return;
        }
      }

      // Enforce limit - destroy oldest if at capacity
      if (this.allVideos.size >= this.getMaxVideosInDOM()) {
        log(`[Video] At limit (${this.allVideos.size}/${this.getMaxVideosInDOM()}), cleaning oldest`);
        this.cleanupOldestVideo();
      }

      // PHASE 2 FIX: On mobile, delay video creation during scrolling
      if (isMobileDevice() && this.isScrolling) {
        log('[Video] Scroll in progress, queuing video creation');
        this.pendingVideoCreations.push({ container, productCard, instance });
        return;
      }

      // Create new video immediately (or after scroll stops on mobile)
      this.createVideoElement(container, productCard, instance);
    },

    handleVideoExitViewport(container, productCard) {
      const video = container.querySelector('video');
      if (!video) return;
      
      // Pause immediately
      if (!video.paused) {
        video.pause();
        log('[Video] Paused on exit');
      }
      
      // PHASE 1 FIX: Always destroy on exit (mobile AND desktop)
      // Prevents accumulation of hidden videos
      this.destroyVideo(container);
      log('[Video] Destroyed on exit (desktop optimization)');
    },

    // ======================================================================
    // VIDEO CREATION - IMMEDIATE & SIMPLE
    // ======================================================================
    createVideoElement(container, productCard, instance) {
      const mediaId = container.dataset.mediaId;
      const productData = productCard.productData;
      
      const mediaArray = productData.media || productData.images;
      if (!mediaArray) {
        log('[Video] No media array found');
        return;
      }

      const mediaItem = mediaArray.find(m => String(m.id) === String(mediaId));
      if (!mediaItem || mediaItem.media_type !== 'video') {
        log('[Video] Media item not found or not video');
        return;
      }

      log(`[Video] Creating video for media ID: ${mediaId}`);

      // Create video element
      const video = document.createElement('video');
      video.className = 'lumistelle-carousel__video-player';
      video.preload = 'metadata';
      video.playsInline = true;
      video.muted = true;
      video.loop = instance.config.videoLoop !== false;
      video.controls = instance.config.videoControlsVisible || false;
      
      if (mediaItem.preview_image) {
        video.poster = mediaItem.preview_image;
      }

      // Add source
      const source = this.selectBestSource(mediaItem.sources);
      if (!source) {
        log('[Video] No suitable source found');
        this.handleVideoError(container);
        return;
      }

      const sourceEl = document.createElement('source');
      sourceEl.src = source.url;
      sourceEl.type = source.mime_type || 'video/mp4';
      video.appendChild(sourceEl);

      // ===== LOADING STATE MANAGEMENT =====
      let loadingStateRemoved = false;
      const removeLoadingState = () => {
        if (loadingStateRemoved) return;
        loadingStateRemoved = true;
        container.classList.remove('is-loading');
        log('[Video] Loading state removed');
      };

      // Multiple fallback events
      video.addEventListener('loadedmetadata', removeLoadingState, { once: true });
      video.addEventListener('canplay', removeLoadingState, { once: true });
      video.addEventListener('loadeddata', removeLoadingState, { once: true });
      
      // Timeout fallback (3 seconds)
      setTimeout(removeLoadingState, 3000);

      // ===== AUTOPLAY WHEN READY =====
      const handleCanPlay = () => {
        if (instance.config.videoAutoplay !== false) {
          this.playVideo(video, container);
        }
      };
      video.addEventListener('canplay', handleCanPlay, { once: true });

      // ===== ERROR HANDLING =====
      video.addEventListener('error', (e) => {
        log('[Video] Error:', e);
        this.handleVideoError(container);
        removeLoadingState();
      });

      // ===== STATE TRACKING =====
      video.addEventListener('playing', () => {
        container.classList.add('is-playing');
        container.classList.remove('is-loading');
        this.activeVideos.set(video, Date.now());
        log('[Video] Playing');
      });

      video.addEventListener('pause', () => {
        container.classList.remove('is-playing');
        this.activeVideos.delete(video);
        log('[Video] Paused');
      });

      // ===== PLAY/PAUSE BUTTON =====
      const playPauseButton = document.createElement('button');
      playPauseButton.className = 'lumistelle-video-play-pause-btn';
      playPauseButton.setAttribute('aria-label', 'Play video');
      playPauseButton.innerHTML = `
        <svg class="icon-play" viewBox="0 0 12 14"><path d="M1.5 1.66217V12.3378C1.5 13.3555 2.54254 13.9612 3.41421 13.4142L10.5858 8.07639C11.4575 7.52933 11.4575 6.47067 10.5858 5.92361L3.41421 0.585786C2.54254 0.0387268 1.5 0.644455 1.5 1.66217Z" fill="currentColor"/></svg>
        <svg class="icon-pause" viewBox="0 0 12 14"><path d="M3.5 1.5C3.5 0.947715 3.05228 0.5 2.5 0.5C1.94772 0.5 1.5 0.947715 1.5 1.5V12.5C1.5 13.0523 1.94772 13.5 2.5 13.5C3.05228 13.5 3.5 13.0523 3.5 12.5V1.5Z" fill="currentColor"/><path d="M10.5 1.5C10.5 0.947715 10.0523 0.5 9.5 0.5C8.94772 0.5 8.5 0.947715 8.5 1.5V12.5C8.5 13.0523 8.94772 13.5 9.5 13.5C10.0523 13.5 10.5 13.0523 10.5 12.5V1.5Z" fill="currentColor"/></svg>
      `;
      
      playPauseButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (video.paused) {
          this.playVideo(video, container);
        } else {
          video.pause();
        }
      });
      
      container.appendChild(playPauseButton);

      // ===== ADD TO DOM =====
      container.appendChild(video);
      container.dataset.videoCreated = 'true';
      container.classList.add('is-loading');
      
      // Track in our set
      this.allVideos.add(video);
      
      // Store creation time on video for cleanup priority
      video.dataset.createdAt = Date.now();
      
      log(`[Video] Created (${this.allVideos.size} total in DOM)`);

      // PHASE 2 FIX: Use loading queue on mobile for sequential loading
      this.queueVideoLoad(video, container);
    },

    selectBestSource(sources) {
      if (!sources || sources.length === 0) return null;

      const mp4Sources = sources.filter(s => s.mime_type === 'video/mp4');
      if (mp4Sources.length > 0) {
        return mp4Sources.reduce((best, current) => {
          return (current.height > best.height) ? current : best;
        });
      }

      return sources.reduce((best, current) => {
        return (current.height > best.height) ? current : best;
      });
    },

    // ======================================================================
    // VIDEO CONTROL
    // ======================================================================
    playVideo(video, container) {
      if (!video || video.readyState < 2) {
        log('[Video] Not ready to play');
        return;
      }

      const playPromise = video.play();
      
      if (playPromise !== undefined) {
        playPromise
          .then(() => {
            log('[Video] Play started');
          })
          .catch((error) => {
            log('[Video] Play failed:', error.message);
            this.showPlayButton(container);
          });
      }
    },

    showPlayButton(container) {
      if (container.querySelector('.lumistelle-video-play-overlay')) return;

      const overlay = document.createElement('div');
      overlay.className = 'lumistelle-video-play-overlay';
      overlay.innerHTML = `
        <button class="lumistelle-video-play-button" aria-label="Play video">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="white">
            <path d="M8 5v14l11-7z"/>
          </svg>
        </button>
      `;

      overlay.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const video = container.querySelector('video');
        if (video) {
          video.muted = false;
          this.playVideo(video, container);
          overlay.remove();
        }
      });

      container.appendChild(overlay);
    },

    handleVideoError(container) {
      container.classList.add('video-error');
      container.classList.remove('is-loading');
      
      const poster = container.querySelector('.lumistelle-video-poster');
      if (poster) {
        poster.style.display = '';
        const errorOverlay = document.createElement('div');
        errorOverlay.className = 'lumistelle-video-error-overlay';
        errorOverlay.textContent = 'Video unavailable';
        poster.appendChild(errorOverlay);
      }
    },

    // ======================================================================
    // CLEANUP - COMPLETE DESTRUCTION
    // ======================================================================
    cleanupOldestVideo() {
      let oldestVideo = null;
      let oldestTime = Date.now();

      // Find oldest video by creation time
      this.allVideos.forEach(video => {
        const createdAt = parseInt(video.dataset.createdAt, 10);
        if (createdAt < oldestTime) {
          oldestTime = createdAt;
          oldestVideo = video;
        }
      });

      if (oldestVideo) {
        const container = oldestVideo.closest('[data-media-type="video"]');
        if (container) {
          log('[Video] Destroying oldest video');
          this.destroyVideo(container);
        }
      }
    },

    destroyVideo(container) {
      const video = container.querySelector('video');
      if (!video) return;
      
      log('[Video] Destroying video');
      
      // Remove from tracking
      this.activeVideos.delete(video);
      this.allVideos.delete(video);
      
      // Stop playback
      video.pause();
      
      // Remove source to stop network activity
      video.src = '';
      video.load();
      
      // Remove video element
      video.remove();
      
      // Remove button
      const button = container.querySelector('.lumistelle-video-play-pause-btn');
      if (button) button.remove();
      
      // Reset container state
      container.dataset.videoCreated = 'false';
      container.classList.remove('is-loading', 'is-playing', 'video-error');
      
      // Show poster again
      const poster = container.querySelector('.lumistelle-video-poster');
      if (poster) {
        poster.style.display = '';
      }
      
      log(`[Video] Destroyed (${this.allVideos.size} remain in DOM)`);
    },

    // ======================================================================
    // HYDRATION RETRY
    // ======================================================================
    retryPendingVideos(productCard, instance) {
      const pendingContainers = productCard.querySelectorAll('[data-pending-hydration="true"]');
      pendingContainers.forEach(container => {
        delete container.dataset.pendingHydration;
        this.handleVideoEnterViewport(container, productCard, instance);
      });
    },

    // ======================================================================
    // EXTERNAL CONTROLS
    // ======================================================================
    handleMediaVisibilityChange(container, isVisible, instance = null) {
      if (!container) return;

      const productDataSource = container.closest('.lumistelle-carousel__product') || 
                                container.closest('[data-section-id]');
      if (!productDataSource) return;

      if (!instance && window.Lumistelle && window.Lumistelle.engine) {
        instance = window.Lumistelle.engine.getInstance(productDataSource);
      }
      if (!instance) return;

      if (isVisible) {
        if (this.systemReady) {
          this.handleVideoEnterViewport(container, productDataSource, instance);
        } else {
          container.dataset.queuedForPlay = 'true';
        }
      } else {
        delete container.dataset.queuedForPlay;
        this.handleVideoExitViewport(container, productDataSource);
      }
    },

    // ======================================================================
    // INSTANCE CLEANUP
    // ======================================================================
    destroyInstance(instance) {
      const element = instance.element || instance.container;
      if (!element) return;
      
      const videoContainers = element.querySelectorAll('[data-media-type="video"]');
      videoContainers.forEach(container => {
        this.destroyVideo(container);
      });

      const instanceId = instance.id || element.id;
      const observer = this.observers.get(instanceId);
      if (observer) {
        observer.disconnect();
        this.observers.delete(instanceId);
      }
      
      // PHASE 1 FIX: Stop cleanup if no more instances
      if (this.observers.size === 0 && this.allVideos.size === 0) {
        this.stopPeriodicCleanup();
      }
      
      log(`[Video] Instance destroyed: ${instanceId}`);
    }
  };

  // Export
  window.LumistelleCarouselVideo = LumistelleCarouselVideo;

})(window, document);


/**
 * Video Performance Monitor v3.0
 * Add ?video-debug to URL to enable
 * Now tracks ACTUAL video count including hidden ones
 */
(function() {
  if (!window.location.search.includes('video-debug')) return;
  
  console.log('%c[Video Debug] Monitoring enabled', 'color: cyan; font-weight: bold;');
  
  const stats = {
    videosInDOM: 0,
    videosVisible: 0,
    videosHidden: 0,
    videosPlaying: 0,
    videosLoading: 0,
    memoryUsage: 0,
    fps: 0,
    createCount: 0,
    destroyCount: 0
  };
  
  // FPS monitoring
  let lastTime = performance.now();
  let frames = 0;
  
  function measureFPS() {
    frames++;
    const currentTime = performance.now();
    if (currentTime >= lastTime + 1000) {
      stats.fps = Math.round((frames * 1000) / (currentTime - lastTime));
      frames = 0;
      lastTime = currentTime;
    }
    requestAnimationFrame(measureFPS);
  }
  measureFPS();
  
  // Track creation/destruction
  const observer = new MutationObserver((mutations) => {
    mutations.forEach(mutation => {
      mutation.addedNodes.forEach(node => {
        if (node.tagName === 'VIDEO') {
          stats.createCount++;
          console.log('%c[Video] Created', 'color: lightgreen', {
            total: document.querySelectorAll('video').length,
            src: node.querySelector('source')?.src
          });
        }
      });
      mutation.removedNodes.forEach(node => {
        if (node.tagName === 'VIDEO') {
          stats.destroyCount++;
          console.log('%c[Video] Destroyed', 'color: orange', {
            remaining: document.querySelectorAll('video').length
          });
        }
      });
    });
  });
  
  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
  
  // Update stats
  function updateStats() {
    const videos = document.querySelectorAll('video');
    stats.videosInDOM = videos.length;
    stats.videosPlaying = 0;
    stats.videosLoading = 0;
    stats.videosVisible = 0;
    stats.videosHidden = 0;
    
    videos.forEach(video => {
      if (!video.paused) stats.videosPlaying++;
      
      const container = video.closest('[data-media-type="video"]');
      if (container) {
        if (container.classList.contains('is-loading')) stats.videosLoading++;
        
        // Check if actually visible
        const rect = container.getBoundingClientRect();
        const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
        
        if (isInViewport && container.offsetParent !== null) {
          stats.videosVisible++;
        } else {
          stats.videosHidden++;
        }
      }
    });
    
    if (performance.memory) {
      stats.memoryUsage = Math.round(performance.memory.usedJSHeapSize / 1048576);
    }
  }
  
  // Overlay
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position: fixed;
    top: 10px;
    right: 10px;
    background: rgba(0, 0, 0, 0.95);
    color: #0f0;
    padding: 12px;
    font-family: 'Courier New', monospace;
    font-size: 11px;
    z-index: 999999;
    border-radius: 4px;
    border: 1px solid #0f0;
    min-width: 240px;
    pointer-events: none;
  `;
  document.body.appendChild(overlay);
  
  // Update display
  setInterval(() => {
    updateStats();
    
    const isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
    const maxVideos = isMobile ? 2 : 6;
    
    const fpsColor = stats.fps >= 55 ? '#0f0' : stats.fps >= 45 ? '#ff0' : '#f00';
    const totalColor = stats.videosInDOM > maxVideos ? '#f00' : '#0f0';
    const hiddenColor = stats.videosHidden > 0 ? '#ff0' : '#0f0';
    const loadingColor = stats.videosLoading > 0 ? '#ff0' : '#0f0';
    
    overlay.innerHTML = `
      <div style="font-weight: bold; margin-bottom: 6px; color: cyan;">VIDEO DEBUG v3</div>
      <div style="font-size: 9px; margin-bottom: 6px; color: #999;">Device: ${isMobile ? 'MOBILE' : 'DESKTOP'} | Max: ${maxVideos}</div>
      <div style="color: ${fpsColor}; font-weight: bold;">FPS: ${stats.fps}</div>
      <div style="color: ${totalColor}; font-weight: bold;">Total Videos: ${stats.videosInDOM}</div>
      <div>Visible: ${stats.videosVisible}</div>
      <div style="color: ${hiddenColor}">Hidden: ${stats.videosHidden}</div>
      <div>Playing: ${stats.videosPlaying}</div>
      <div style="color: ${loadingColor}">Loading: ${stats.videosLoading}</div>
      <div style="margin-top: 6px; font-size: 9px;">Created: ${stats.createCount} | Destroyed: ${stats.destroyCount}</div>
      ${stats.memoryUsage ? `<div style="margin-top: 6px;">Memory: ${stats.memoryUsage}MB</div>` : ''}
    `;
  }, 500);
  
  // Detailed log
  setInterval(() => {
    updateStats(); // Ensure stats are current
    console.log('[Video Debug] Stats:', {...stats});
    
    if (stats.videosHidden > 0) {
      console.warn(`[Video Debug] WARNING: ${stats.videosHidden} hidden videos detected!`);
    }
    
    // PHASE 1 FIX: Properly reference maxVideos from local scope
    const isMobile = /Android|webOS|iPhone|iPad|iPod/i.test(navigator.userAgent) || window.innerWidth < 768;
    const maxVideos = isMobile ? 2 : 6;
    
    if (stats.videosInDOM > maxVideos) {
      console.error(`[Video Debug] ERROR: ${stats.videosInDOM} videos in DOM (limit: ${maxVideos})`);
    }
  }, 3000);
})();